import Modal from "@/components/atoms/Modal";
import AvailabilityExceptionsEditor from "@/components/molecules/availability/AvailabilityExceptionsEditor";
import AvailabilityMonthPreview from "@/components/molecules/availability/AvailabilityMonthPreview";
import WeeklyScheduleEditor from "@/components/molecules/availability/WeeklyScheduleEditor";
import { INVALID_FORM_MESSAGE } from "@/constants/validatorMessages";
import { emptyWeek, scheduleErrors } from "@/helpers/availability";
import {
  AvailabilityConflict,
  AvailabilityException,
  WeeklyDay,
} from "@/interfaces/Availability";
import { IModalMethods } from "@/interfaces/Modal";
import {
  useGetUserAvailabilityQuery,
  useUpdateUserAvailabilityMutation,
} from "@/services/availability";
import { faClock } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DateTime } from "luxon";
import {
  ElementRef,
  FormEvent,
  ForwardRefRenderFunction,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";

interface IProps {
  userId: string;
  title: string;
  /** Lecture seule : ni plage, ni exception, ni bouton Enregistrer. */
  readOnly?: boolean;
}

type SaveError = {
  status?: number;
  data?: {
    conflicts?: AvailabilityConflict[];
    errors?: { field: string; message: string }[];
  };
};

const parisToday = () => DateTime.now().setZone("Europe/Paris").toISODate()!;

const AvailabilityModal: ForwardRefRenderFunction<IModalMethods, IProps> = (
  { userId, title, readOnly = false },
  ref,
) => {
  const ModalRef = useRef<ElementRef<typeof Modal>>(null);
  const [open, setOpen] = useState(false);
  const today = useMemo(parisToday, [open]);

  const { currentData } = useGetUserAvailabilityQuery(userId, {
    skip: !open,
    refetchOnMountOrArgChange: true,
  });
  const [updateAvailability, { isLoading }] =
    useUpdateUserAvailabilityMutation();

  const [slotMinutes, setSlotMinutes] = useState(60);
  const [weekly, setWeekly] = useState<WeeklyDay[]>(emptyWeek);
  const [exceptions, setExceptions] = useState<AvailabilityException[]>([]);
  const [openException, setOpenException] = useState<number | null>(null);
  const [month, setMonth] = useState(() => parisToday().slice(0, 7));
  const [conflicts, setConflicts] = useState<AvailabilityConflict[] | null>(
    null,
  );
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const unsynchronizedCount = currentData?.unsynchronizedMeetingsCount ?? 0;

  useImperativeHandle(ref, () => ({
    showOrHiddenModal: () => {
      setOpen((wasOpen) => !wasOpen);
      ModalRef.current?.showOrHiddenModal();
    },
  }));

  // Chaque ouverture repart du planning enregistré : une saisie abandonnée ne
  // doit pas réapparaître à la suivante.
  useEffect(() => {
    if (!open || !currentData) return;
    setSlotMinutes(currentData.slotMinutes ?? 60);
    setWeekly(currentData.weekly);
    setExceptions(currentData.exceptions);
    setOpenException(null);
    setConflicts(null);
    setServerErrors({});
    setMonth(today.slice(0, 7));
  }, [open, currentData, today]);

  const errors = useMemo(
    () => ({ ...serverErrors, ...scheduleErrors(weekly, exceptions) }),
    [serverErrors, weekly, exceptions],
  );

  const onWeeklyChange = (value: WeeklyDay[]) => {
    setWeekly(value);
    setServerErrors({});
    setConflicts(null);
  };
  const onExceptionsChange = (value: AvailabilityException[]) => {
    setExceptions(value);
    setServerErrors({});
    setConflicts(null);
  };

  const save = (force: boolean) => {
    updateAvailability({
      userId,
      body: { weekly, exceptions, force, slotMinutes },
    })
      .unwrap()
      .then(() => {
        toast.success("Disponibilités enregistrées.");
        ModalRef.current?.showOrHiddenModal();
      })
      .catch((error: SaveError) => {
        if (error?.status === 409 && error.data?.conflicts) {
          setConflicts(error.data.conflicts);
          return;
        }
        if (error?.status === 422 && error.data?.errors) {
          setServerErrors(
            Object.fromEntries(
              error.data.errors.map((e) => [e.field, e.message]),
            ),
          );
          toast.error(INVALID_FORM_MESSAGE);
          return;
        }
        if (error?.status === 403) {
          toast.error(
            "Vous n'avez pas le droit de modifier ces disponibilités.",
          );
          return;
        }
        toast.error("Une erreur est survenue");
      });
  };

  const onSubmit = (event?: FormEvent) => {
    event?.preventDefault();
    const fields = Object.keys(errors);
    if (fields.length) {
      // Une erreur d'exception reste invisible tant que sa ligne est repliée.
      const faulty = fields.find((field) => field.startsWith("exceptions."));
      if (faulty) setOpenException(Number(faulty.split(".")[1]));
      toast.error(INVALID_FORM_MESSAGE);
      return;
    }
    save(false);
  };

  const onDayClick = (date: string) => {
    const index = exceptions.findIndex(
      (e) => e.startDate <= date && date <= e.endDate,
    );
    if (index >= 0) {
      setOpenException(index);
      return;
    }
    onExceptionsChange([
      ...exceptions,
      { startDate: date, endDate: date, isOff: true, ranges: [] },
    ]);
    setOpenException(exceptions.length);
  };

  return (
    <Modal
      ref={ModalRef}
      title={title}
      isLoading={isLoading}
      continueTitle={
        unsynchronizedCount > 0 ? "Enregistrer et synchroniser" : "Enregistrer"
      }
      onHide={() => setOpen(false)}
      onSubmit={readOnly ? undefined : onSubmit}
    >
      {!currentData ? (
        <p className="fs--1 text-600 mb-0" role="status">
          Chargement des disponibilités…
        </p>
      ) : (
        <div className="fx-availability">
          <div className="fx-availability__intro">
            <p>
              Un rendez-vous ne peut être posé que pendant ces horaires. Les
              jours fériés sont fermés automatiquement.
            </p>
            <span className="fx-availability__chip">
              <FontAwesomeIcon icon={faClock} />
              Heure de Paris
            </span>
            {!currentData.setAt && (
              <span className="fx-availability__chip fx-availability__chip--warning">
                Jamais enregistrées
              </span>
            )}
          </div>

          <div className="fx-availability__grid">
            <div className="fx-availability__panel">
              <div className="mb-3">
                <label htmlFor="appointment-duration" className="form-label">
                  Durée d’un rendez-vous
                </label>
                {readOnly ? (
                  <p>
                    {slotMinutes < 60
                      ? `${slotMinutes} min`
                      : `${Math.floor(slotMinutes / 60)} h${
                          slotMinutes % 60 ? ` ${slotMinutes % 60}` : ""
                        }`}
                  </p>
                ) : (
                  <select
                    id="appointment-duration"
                    className="form-select"
                    value={slotMinutes}
                    onChange={(event) =>
                      setSlotMinutes(Number(event.target.value))
                    }
                  >
                    {[
                      15, 20, 30, 45, 60, 75, 90, 105, 120, 135, 150, 165, 180,
                    ].map((minutes) => (
                      <option key={minutes} value={minutes}>
                        {minutes < 60
                          ? `${minutes} min`
                          : `${Math.floor(minutes / 60)} h${
                              minutes % 60 ? ` ${minutes % 60}` : ""
                            }`}
                      </option>
                    ))}
                  </select>
                )}
                <p className="fs--1 text-600 mt-1">
                  Enregistrer applique cette durée à tous les rendez-vous de
                  cette personne, y compris ceux déjà réservés, sans changer
                  leur heure de début.
                </p>
                {unsynchronizedCount > 0 && (
                  <div role="alert" className="alert alert-warning mb-0">
                    <p className="mb-1">
                      {unsynchronizedCount === 1
                        ? "1 rendez-vous a une durée différente"
                        : `${unsynchronizedCount} rendez-vous ont une durée différente`}{" "}
                      des {currentData.slotMinutes ?? 60} minutes enregistrées.
                    </p>
                    {!readOnly && (
                      <p className="mb-0">
                        Enregistrez pour appliquer la durée sélectionnée à tous
                        les rendez-vous, passés et à venir, même si vous ne
                        changez pas ce réglage. Leurs heures de début restent
                        identiques.
                      </p>
                    )}
                  </div>
                )}
              </div>
              <WeeklyScheduleEditor
                value={weekly}
                onChange={onWeeklyChange}
                errors={errors}
                readOnly={readOnly}
              />
              <hr className="fx-availability__divider" />
              <AvailabilityExceptionsEditor
                value={exceptions}
                onChange={onExceptionsChange}
                errors={errors}
                openIndex={openException}
                onOpenChange={setOpenException}
                today={today}
                weekly={weekly}
                readOnly={readOnly}
              />
            </div>

            <div className="fx-availability__panel">
              <AvailabilityMonthPreview
                weekly={weekly}
                exceptions={exceptions}
                today={today}
                month={month}
                onMonthChange={setMonth}
                onDayClick={readOnly ? undefined : onDayClick}
              />
            </div>
          </div>

          {conflicts && (
            <div role="alert" className="fx-conflicts">
              <p className="fx-conflicts__title">
                {conflicts.length > 1
                  ? `${conflicts.length} rendez-vous tombent hors des nouvelles disponibilités`
                  : "Un rendez-vous tombe hors des nouvelles disponibilités"}
              </p>
              <ul>
                {conflicts.map((conflict) => (
                  <li key={conflict.meetingId}>
                    {DateTime.fromISO(conflict.startAt)
                      .setLocale("fr")
                      .toFormat("cccc d LLLL 'à' HH:mm")}{" "}
                    — {conflict.label} (
                    {conflict.calendarType === "montage"
                      ? "montage"
                      : "commercial"}
                    )
                  </li>
                ))}
              </ul>
              <p className="fx-conflicts__note">
                Enregistrer ne les déplace pas : il faudra les déplacer ou les
                réassigner ensuite.
              </p>
              <div className="fx-conflicts__actions">
                <Button variant="link" onClick={() => setConflicts(null)}>
                  Revenir à l'édition
                </Button>
                <Button
                  variant="warning"
                  disabled={isLoading}
                  onClick={() => save(true)}
                >
                  Enregistrer quand même
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default forwardRef(AvailabilityModal);
